import React from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Droplet, Warehouse, Truck } from "lucide-react";

export default function Inventory() {
  const { data: summary } = useQuery({ queryKey: ["inv-summary"], queryFn: () => api.get("/inventory/summary").then((r) => r.data) });
  const { data: tanksData } = useQuery({ queryKey: ["tanks"], queryFn: () => api.get("/inventory/tanks").then((r) => r.data) });
  const { data: fleet } = useQuery({ queryKey: ["fleet-bowsers"], queryFn: () => api.get("/fleet/bowsers").then((r) => r.data) });
  const { data: moves } = useQuery({ queryKey: ["movements"], queryFn: () => api.get("/inventory/movements?limit=15").then((r) => r.data) });

  return (
    <div className="space-y-6" data-testid="inventory-page">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight">Inventory</h1>
        <p className="text-sm text-muted-foreground">Diesel across depots and bowsers.</p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <KPI icon={Droplet} label="Total diesel" value={`${Math.round(summary?.total_diesel_litres || 0)} L`} />
        <KPI icon={Warehouse} label="Tank stock" value={`${Math.round(summary?.tank_total || 0)} / ${summary?.tank_capacity || 0} L`} />
        <KPI icon={Truck} label="Bowser stock" value={`${Math.round(summary?.bowser_total || 0)} / ${summary?.bowser_capacity || 0} L`} />
      </div>

      <Card>
        <CardHeader><CardTitle>Depot tanks</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {(tanksData?.items || []).map((t) => {
            const pct = t.capacity_litres ? Math.min(100, (t.current_stock / t.capacity_litres) * 100) : 0;
            return (
              <div key={t.id} className="rounded-lg border border-border/60 p-4">
                <div className="flex items-center justify-between">
                  <div className="font-mono text-sm font-bold">{t.code}</div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">{t.location}</div>
                </div>
                <div className="mt-1 font-display text-lg font-black">{t.name}</div>
                <div className="mt-3 flex items-baseline justify-between">
                  <div className="font-display text-2xl font-black text-primary">{Math.round(t.current_stock)}L</div>
                  <div className="text-sm text-muted-foreground">of {t.capacity_litres}L</div>
                </div>
                <Progress value={pct} className="mt-2 h-2" />
                <div className="mt-1 text-xs text-muted-foreground">{Math.round(pct)}% full · Min {t.min_level}L</div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Bowsers · current stock</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4">
          {(fleet?.items || []).map((b) => {
            const pct = b.capacity_litres ? Math.min(100, (b.current_stock / b.capacity_litres) * 100) : 0;
            return (
              <div key={b.id} className="rounded-lg border border-border/60 p-3">
                <div className="flex items-center justify-between">
                  <div className="font-mono text-sm font-bold">{b.code}</div>
                  <div className="text-xs text-muted-foreground">{b.registration_no}</div>
                </div>
                <div className="mt-2 text-xl font-bold font-mono">{Math.round(b.current_stock || 0)}L</div>
                <Progress value={pct} className="mt-1 h-1.5" />
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Recent stock movements</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="data-table w-full">
            <thead className="border-b border-border/60 bg-muted/30 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr><th className="px-4 py-2">Time</th><th className="px-4 py-2">Entity</th><th className="px-4 py-2">Type</th><th className="px-4 py-2 text-right">Qty</th><th className="px-4 py-2">By</th></tr>
            </thead>
            <tbody>
              {(moves?.items || []).map((m) => (
                <tr key={m.id} className="border-b border-border/40 text-sm">
                  <td className="px-4 py-2 text-xs">{new Date(m.created_at).toLocaleString()}</td>
                  <td className="px-4 py-2 text-xs">{m.entity_type} · {m.entity_id.slice(0, 8)}</td>
                  <td className="px-4 py-2 font-mono text-xs uppercase">{m.movement_type}</td>
                  <td className="px-4 py-2 text-right font-mono">{m.quantity}L</td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{m.performed_by}</td>
                </tr>
              ))}
              {(!moves?.items || moves.items.length === 0) && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-sm text-muted-foreground">No movements yet — happens automatically on load/delivery.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

const KPI = ({ icon: Icon, label, value }) => (
  <div className="kpi-card">
    <div className="flex items-center justify-between text-muted-foreground">
      <div className="text-xs uppercase tracking-widest">{label}</div>
      <Icon size={16} />
    </div>
    <div className="mt-2 font-display text-2xl font-black">{value}</div>
  </div>
);
